import { Injectable } from '@angular/core';
import { ApiService } from '@core/services/api.service';
import { environment } from 'environments/environment';
import {
  of,
  Observable
} from 'rxjs';
import {
  catchError,
  map
} from 'rxjs/operators';
import { stringifyIfJSON } from '../utils';

@Injectable({
  providedIn: 'root',
})
export class AnalitikaService {
  private readonly keszletkartonEndpoint = 'keszlet_karton';
  private readonly foglalasListaEndpoint = 'foglalas_lista';
  private readonly vevoAnalitikaEndpoint = 'vevo_analitika';
  private readonly szallitoAnalitikaEndpoint = 'szallito_analitika';
  private readonly minKeszletEndpoint = 'min_keszlet';
  private readonly bizonylatAnalitikaEndpoint = 'bizonylat_analitika';
  private readonly napiPenztarZarasEndpoint = 'napi_penztar_zaras';
  private readonly bizonylatokTeljesitettsegeEnpoint = 'bizonylatok_teljesitettsege';
  private readonly koltsegnemenkentAnalitikaEndpoint = 'koltsegnemenkenti_analitika';
  private readonly forgalomAnalitikaEnpoint = 'forgalom';
  private readonly jutalekEndpoint = 'jutalek';
  private readonly afaAnalitikaEndpoint = 'afa_analitika';
  private readonly arajanlatAnalitikaEndpoint = 'arajanlat_analitika';
  private readonly kimenoKifizetesekEndpoint = 'kimeno_szamla_kifizetesek';
  private readonly szallitoTetelAnalitikaEndpoint = 'szallitolevel';
  private readonly kimenoEndpoint = 'kimenoszamla_bevetel_kat';
  private readonly szallitoiEndpoint = 'szallitoi_szamla';
  private readonly havimunkaidoEndpoint = 'havi_mido';
  private readonly munkatarsNevEndpoint = 'munkatarsak_nevre';
  private readonly munkatarsSzabadsagNevEndpoint = 'szabadsag_munkatars';
  private readonly munkatarsSzabadsagOEEndpoint = 'szabadsag_oe_re';

  private readonly munkatarsakBeosztasEndpoint = 'munkatarsak_beosztas';
  private readonly munkatarsakEndpoint = 'munkatarsak';


  constructor(
    private api: ApiService,
  ) {}

  generateKeszletkarton(payload: any): Observable<any> {
    return this.api
      .post(`${environment.xlsGeneralas}/${this.keszletkartonEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateFoglalasLista(raktarId: string): Observable<any> {
    return this.api
      .post(`${environment.xlsGeneralas}/${this.foglalasListaEndpoint}/${raktarId}`, '')
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateVevoAnalitika(oe_id: any, datum_kezd: any, datum_veg: any, datum_szerint: any): Observable<any> {
    const payload = {
      oe: oe_id,
      datum_kezd,
      datum_veg,
      datum_szerint
    }

    return this.api
      .post(`${environment.xlsGeneralas}/${this.vevoAnalitikaEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateSzallitoAnalitika(startDate: string, endDate: string, oe_id: any, datum_szerint: any): Observable<any> {
    const payload = {
      oe: oe_id,
      datum_kezd: startDate,
      datum_veg: endDate,
      datum_szerint
    }

    return this.api
      .post(`${environment.xlsGeneralas}/${this.szallitoAnalitikaEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateSzallitoTetelAnalitika(startDate: string, endDate: string, oe_id: any): Observable<any> {
    return this.api
      .post(`${environment.xlsGeneralas}/${this.szallitoTetelAnalitikaEndpoint}/${startDate}/${endDate}/${oe_id}`, '')
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateMinKeszlet(raktarId: string): Observable<any> {
    return this.api
      .post(`${environment.xlsGeneralas}/${this.minKeszletEndpoint}/${raktarId}`, '')
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateBizonylatAnalitika(ev: number, oe: any, datum_szerint: any): Observable<any> {
    const payload = {
      oe: stringifyIfJSON(oe),
      ev: ev,
      datum_szerint
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.bizonylatAnalitikaEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateNapiPenztarZaras(datum: any, oe: any): Observable<any> {
    return this.api
      .post(`${environment.xlsGeneralas}/${this.napiPenztarZarasEndpoint}/${datum}/${oe}`, '')
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateBizonylatokTeljesitettsege(ev: number, oe: any): Observable<any> {
    const payload = {
      oe,
      ev
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.bizonylatokTeljesitettsegeEnpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateKoltsegnemAnalitika(datum_kezd: any, datum_veg: any, oe: any, datum_szerint: any): Observable<any> {
    const payload = {
      datum_kezd,
      datum_veg,
      oe,
      datum_szerint
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.koltsegnemenkentAnalitikaEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateKimenoAnalitika(datum_kezd: any, datum_veg: any, oe: any, bevetel_kat: any, koltseghely: any, datum_szerint: any): Observable<any> {
    const payload = {
      datum_kezd,
      datum_veg,
      oe,
      bevetel_kat,
      koltseghely,
      datum_szerint
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.kimenoEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateSzallitoiAnalitika(datum_kezd: any, datum_veg: any, oe: any, koltsegkategoria: any, koltseghely: any, datum_szerint: any): Observable<any> {
    const payload = {
      datum_kezd,
      datum_veg,
      oe,
      koltsegkategoria,
      koltseghely,
      datum_szerint
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.szallitoiEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateForgalomAnalitika(startDate: string, endDate: string, oe: string): Observable<any> {
    const payload = {
      oe: oe,
      datum_kezd: startDate,
      datum_veg: endDate
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.forgalomAnalitikaEnpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateJutalekAnalitika(ev: number): Observable<any> {
    return this.api
      .post(`${environment.xlsGeneralas}/${this.jutalekEndpoint}/${ev}`, '')
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateAfaAnalitika(ev: number, honap: number): Observable<any> {
    return this.api
      .post(`${environment.xlsGeneralas}/${this.afaAnalitikaEndpoint}/${ev}/${honap}`, '')
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateAfaAnalitikaIntervall(startDate: string, endDate: string, oe: string, datum_szerint: string): Observable<any> {
    return this.api
      .post(`${environment.xlsGeneralas}/${this.afaAnalitikaEndpoint}/${startDate}/${endDate}/${oe}/${datum_szerint}`, '')
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateKimenoKifizetesekAnalitika(ev: number, honap: number, oe_id: any): Observable<any> {
    const payload = {
      ev,
      honap,
      oe: oe_id
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.kimenoKifizetesekEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateArajanlatAnalitika(ev: number): Observable<any> {
    return this.api
      .post(`${environment.xlsGeneralas}/${this.arajanlatAnalitikaEndpoint}/${ev}`, '')
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateKimenoSzamlaKifizetesekAnalitika(ev: number, honap: number, oe: any): Observable<any> {
    const payload = {
      oe,
      ev,
      honap
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.kimenoKifizetesekEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateHavimunkaidoAnalitika(ev: number, honap: number, oe: any): Observable<any> {
    const payload = {
      oe_id: oe,
      ev,
      honap
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.havimunkaidoEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateMunkatarsakBeosztasAnalitika(oe_id: any, datum_kezd: any, datum_veg: any): Observable<any> {
    const payload = {
      oe_id: oe_id,
      datumkezd: datum_kezd,
      datumveg: datum_veg,
    }

    return this.api
      .post(`${environment.xlsGeneralas}/${this.munkatarsakBeosztasEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateMunkatarsAnalitika(ev: number, honap: number, oe: any): Observable<any> {
    const payload = {
      oe_id: oe,
      ev,
      honap
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.munkatarsakEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateMunkatarsNevAnalitika(vezeteknev: string, keresztnev: string): Observable<any> {
    const payload = {
      vezeteknev,
      keresztnev,
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.munkatarsNevEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateMunkatarsSzabadsagNevAnalitika(vezeteknev: string, keresztnev: string): Observable<any> {
    const payload = {
      vezeteknev,
      keresztnev,
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.munkatarsSzabadsagNevEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generateMunkatarsSzabadsagOeAnalitika(oe_id: any): Observable<any> {
    const payload = {
      oe_id
    }
    return this.api
      .post(`${environment.xlsGeneralas}/${this.munkatarsSzabadsagOEEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }
}
